import {useMemo} from 'react';

import {serverAPI} from 'features/server/api/serverAPI';
import {serverAlertRuleAPI} from 'features/server/api/serverAlertRuleAPI';
import {serverIncidentAPI} from 'features/server/api/serverIncidentAPI';
import {SERVER_INCIDENT_FILTER_KEYS} from 'features/server/lib/serverFilters';
import {usePlugState, PlugState, useHasTariffLimit} from 'entities/user';
import useListParams from 'shared/lib/hooks/useListParams';
import useResetPageOnEmpty from 'shared/lib/hooks/useResetPageOnEmpty';
import {getPollingInterval} from 'shared/api/pollingHelper';
import IPagination from 'shared/api/model/IPagination';
import IOrderBy from 'shared/model/IOrderBy';
import {SetOrderByType} from 'shared/ui/Table/Table';

import IServerOutput from './Server/IServer';
import IServerIncident from './ServerIncident/IServerIncident';
import {IServerAlertRuleMetricOption} from './ServerAlertRule/IServerAlertRuleRecommendations';
import useCanCreateServer from './useCanCreateServer';

const EMPTY_SERVERS: IServerOutput[] = [];
const EMPTY_METRIC_OPTIONS: IServerAlertRuleMetricOption[] = [];

export interface IUseServerIncidents {
    pagination: IPagination<IServerIncident> | undefined,
    isLoading: boolean,
    isFetching: boolean,
    refetch: () => void,
    search: string,
    onSearch: (_search: string) => void,
    sort: IOrderBy | undefined,
    setSort: SetOrderByType,
    servers: IServerOutput[],
    metricOptions: IServerAlertRuleMetricOption[],
    openCount: number,
    closedCount: number,
    plugState: PlugState,
    canCreateServer: boolean,
}

export const useServerIncidents = (): IUseServerIncidents => {
    const {args, sort, setSort, hasFilters, search, onSearch} = useListParams({
        filterKeys: SERVER_INCIDENT_FILTER_KEYS,
    });

    const hasAccess = useHasTariffLimit('server_max_count_limit');

    const {data: serversPagination} = serverAPI.useGetServersQuery({}, {skip: !hasAccess});
    const servers = useMemo(() => serversPagination?.items ?? EMPTY_SERVERS, [serversPagination]);

    const {data: pagination, isLoading, isFetching, refetch} = serverIncidentAPI.useGetServerIncidentsQuery(
        args,
        {skip: !hasAccess, pollingInterval: getPollingInterval()},
    );
    const {data: openPagination} = serverIncidentAPI.useGetServerIncidentsQuery(
        {filters: ['exists[closed_at]=false'], limit: 1},
        {skip: !hasAccess, pollingInterval: getPollingInterval()},
    );
    const {data: closedPagination} = serverIncidentAPI.useGetServerIncidentsQuery(
        {filters: ['exists[closed_at]=true'], limit: 1},
        {skip: !hasAccess, pollingInterval: getPollingInterval()},
    );
    const {data: recommendations} =
        serverAlertRuleAPI.useGetServerAlertRuleRecommendationsQuery(undefined, {skip: !hasAccess});

    const isRedirecting = useResetPageOnEmpty(pagination);

    const plugState = usePlugState({
        tariffField: 'server_max_count_limit',
        isLoading: isLoading || isRedirecting,
        isEmpty: pagination?.items?.length === 0 && pagination?.totalItems === 0,
        hasFilters,
    });

    const canCreateServer = useCanCreateServer({serversCount: serversPagination?.totalItems ?? 0});

    return {
        pagination,
        isLoading: isLoading || isRedirecting,
        isFetching,
        refetch,
        search,
        onSearch,
        sort,
        setSort,
        servers,
        metricOptions: recommendations?.metrics ?? EMPTY_METRIC_OPTIONS,
        openCount: openPagination?.totalItems ?? 0,
        closedCount: closedPagination?.totalItems ?? 0,
        plugState,
        canCreateServer,
    };
};

export default useServerIncidents;
